import React, {useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useHistory, useLocation} from "react-router-dom";
import {Helmet} from "react-helmet";
import {LAST_QUERY_USAGE_KEY} from "../patientProfile/PatientProfileHeader";
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Dropdown,
    DropdownButton,
    Form,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";

import ExportSummary from "./ExportSummary";

import ListView from "./components/patientList";
import CustomPagination from "../common/CustomPaginaton";

import {
    cancelRequest,
    deleteUserWorklist,
    removePatientsFromList,
    searchPatientMain,
} from "../../actions/patientSearch/patientSearchActions";
import {removeQuicklistById} from "../../actions/navigation/navigationActions";
import {useDispatch} from "react-redux";
import {FilterParam, PatientSearchDTO} from "../../dtos/PatientSearchDTO";
import axios from "../../utils/apiClient";
import Modals from "../../components/modals";
import SpinnerLoading from "../common/components/spinner";
import {
    formatDate,
    getCustomListName,
    getCustomWorklistName,
    getPatientProfileLink,
    getQuickListId,
    preventiveCarePatientFilterParams,
} from "../../utils/util";
import ShareWorklist from "./ShareWorklist";
import _ from "lodash";
import {AppPropertiesUtil, SessionStorageUtil} from "../../utils/localStorageUtil";
import {QuickListContext} from "./QuickListContext";
import {CE_RESTORE_OBJ} from "../common/MainLayout";
import AssignCareManagerModal from "../caremgmt/AssignCareManagerModal";
import {assignCareManager} from "../../actions/caremgmt/hubWorkCenterAction";

import CustomFilterList from "./../../views/quickList/CustomFilterList";
import UpdateCustomFilterList from "./../../views/quickList/UpdateCustomFilterList";
import {FilterGroupNew} from "../../components/filters/filterGroupNew";

const isEmpty = require("is-empty");

export const PRINT_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/print";
export const PRINT_SYNC_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/print-sync";
export const EXPORT_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/export";
export const SUMMARY_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/patient-card/export-summary";
export const CUSTOM_LIST_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/custom-filtered-list/custom-lists";
export const WORKLIST_URL = process.env.REACT_APP_REST_API_BASE_URL + "/v1/custom-work-list/work-list";
const LAST_QUERY_SETTINGS_SUFFIX = `-QLQuerySettings`;
const RECENT_QUERY_SETTINGS_KEY = `ce-recent-query-settings-key`;
const SEARCH_DELAY_MS = 600;
const MIN_SEARCH_LENGTH = 3;
const hasNumericQuicklistId = (quicklistId) => quicklistId && !Number.isNaN(Number(quicklistId));

const buildReturnToMyPatientsUrl = (pathName, quicklistId, customListName) => {
  let workingPathName = pathName.endsWith("/") ? pathName.substring(0, pathName.length - 1) : pathName;
  if (hasNumericQuicklistId(quicklistId)) {
    return encodeURIComponent(
      "returnTo_" +
        workingPathName +
        encodeURIComponent("?loadLastQuerySettings=true&id=" + quicklistId + "&customListName=" + customListName)
    );
  }

  return encodeURIComponent("returnTo_" + workingPathName + "?loadLastQuerySettings=true");
};

const getFilterValue = (values) => {
  let filterValue = "";
  if (Array.isArray(values)) {
    for (const value of values) {
      if (filterValue !== "") {
        filterValue = filterValue + ", ";
      }
      filterValue = filterValue + value;
    }
  } else {
    filterValue = values;
  }
  return filterValue;
};

const getCurrentFilters = (filters, filterParams) => {
  let filterView = [];
  for (const filter of filters) {
    let filterValue = "";
    for (const filterParam of filterParams) {
      if (filter.getKey() === filterParam.paramName) {
        if (filterParam.paramValue.length > 1) {
          filterValue = "Multiple";
        } else if (
          filterParam.displayValue != null &&
          (filterParam.displayValue[0] != null || filterParam.displayValue[0] !== undefined)
        ) {
          filterValue = getFilterValue(filterParam.displayValue[0]);
        } else {
          filterValue = getFilterValue(filterParam.paramValue[0]);
        }
        break;
      }
    }
    if (filterValue === "") {
      filterValue = "All";
    }
    filterView.push({
      filterName: filter.getHeader(),
      filterValue: filterValue,
    });
  }
  return filterView;
};

const safeStringify = (value) => {
  const seen = new WeakSet();
  return JSON.stringify(value, (key, nestedValue) => {
    if (nestedValue && typeof nestedValue === "object") {
      if (seen.has(nestedValue)) {
        return "[Circular]";
      }
      seen.add(nestedValue);
    }
    return nestedValue;
  });
};

const componentName = "QuickList";

const RemoveModalBody = ({ patientsSelected }) => {
  const location = useLocation();

  return (
    <div>
      <span>
        You have selected {patientsSelected.length} member
        {patientsSelected.length > 1 && "s"} to remove from your organiazation's patient list.
      </span>
      <br />
      <br />
      <span>Would you like to proceed?</span>
      <br />
      {patientsSelected.map((patient, index) => (
        <a
          key={patient.id ?? `${patient.lastName}-${patient.firstName}-${index}`}
          className="nav-link pt-0 pb-0"
          href={getPatientProfileLink(patient.id, location, "PreventiveCare")}
        >
          {patient.lastName}, {patient.firstName}
        </a>
      ))}
    </div>
  );
};

RemoveModalBody.propTypes = {
  patientsSelected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    })
  ).isRequired,
};

const ConfirmWorklistDeletion = ({ showConfirmation, setConfirmation, id, customListDto }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  let modalTitle = "Delete Custom List";
  let modalMessage = "Are you sure you want to delete this custom list?";

  const handleWorklistDeletion = async () => {
    await deleteUserWorklist(id);
    let listId = null;
    if (customListDto) {
      listId = "/" + customListDto.quickList.toLowerCase() + "?id=" + id;
    }
    dispatch(removeQuicklistById(listId));
    setConfirmation(false);
    history.push("/all_patients/");
  };

  return (
    <Modals
      show={showConfirmation}
      onHide={() => setConfirmation(false)}
      title={modalTitle}
      size="md"
      onCreate={handleWorklistDeletion}
      hideClickButton={false}
      showExtraButton={false}
      hideFooter={false}
      completeBtn="Delete"
      completeBtnId={`${componentName}-deleteButton`}
      body={<div>{modalMessage}</div>}
    />
  );
};

ConfirmWorklistDeletion.propTypes = {
  showConfirmation: PropTypes.bool,
  setConfirmation: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customListDto: PropTypes.shape({
    quickList: PropTypes.string,
  }),
};

const VIEW = { card: "CARD", list: "LIST" };

const QuickLists = ({
  titleProp,
  filters,
  pageNumberProp,
  pageSizeProp,
  totalSizeProp,
  defaultFilterParamsProp,
  defaultSortParamsProp,
  exportPatientsUrl,
  quickListType,
  displayExtraRows,
  quickListId,
  /*
  cardViewActionObj and listViewActionObj allow the quick list page to pass in a query for the card view and list view.
  You can have 1 query for both card view and list view or you can have 2 separate queries 1 for card view and 1 for list view.

  If page doesn't pass in a cardViewActionObj(will use default query) or passes in a custom cardViewActionObj,
  only 1 query will execute and both card view and list view will be based of that 1 query.

  On other hand if you pass listViewActionObj, 2 async queries will execute; cardViewActionObj for card view and listViewActionObj for list view.

  cardViewActionObj and listViewActionObj have fetchData field for making query to get data and
  copyMap field is to define how to copy data from return set of query to
  patientList, totalSize, pageNumber, and pageSize. copyMap field allows us to handle any return set from a query with no code change.
  */
  cardViewActionObj = {
    fetchData: searchPatientMain,
    copyMap: {
      list: "patientCardDtos",
      totalSize: "totalSize",
      pageSize: "pageSize",
      pageNumber: "pageNumber",
    },
  },
  unScheduledActionObj = {
    fetchData: searchPatientMain,
    copyMap: {
      list: "patientCardDtos",
      totalSize: "totalSize",
      pageSize: "pageSize",
      pageNumber: "pageNumber",
    },
  },
  listViewActionObj,
  startingView = VIEW.card,
  listViewColumns,
  children,
  careManagementActions,
  onCareManagerAssigmentError,
  careManagerPrograms,
  careManagerTeams,
  showCount = false,
}) => {
  const location = useLocation();
  const qlsSettings = useContext(QuickListContext);
  let quicklistId = getQuickListId();
  let customWorklistName = getCustomWorklistName();
  let customListName = getCustomListName();
  const returnToMyPatientsUrl = buildReturnToMyPatientsUrl(location.pathname, quicklistId, customListName);

  const [selectedPatients, setSelectedPatients] = useState(new Map());
  const [title, setTitle] = useState(titleProp);
  const [patientsList, setPatientsList] = useState({
    default: [],
    scheduledPatients: [],
    unScheduledPatients: [],
    unableToContact: [],
    patientRefused: [],
    leftMessage: [],
    changedPCP: [],
  });
  const [cardViewData, setCardViewData] = useState({});
  const [listViewData, setListViewData] = useState({});
  const [pageNumber, setPageNumber] = useState(pageNumberProp);
  const [pageSize, setPageSize] = useState(pageSizeProp);
  const basePageSize = pageSizeProp;
  const [totalSize, setTotalSize] = useState(totalSizeProp);
  const [initialLoad, setInitialLoad] = useState(false);

  const scheduledPatientSize = {};
  const unScheduledPatientSize = {};
  const unableToContactPatientSize = {};
  const patientRefusedPatientSize = {};
  const leftMessagePatientSize = {};
  const changedPCPPatientSize = {};
  const [loadingData, setLoadingData] = useState(true);
  const [listViewLoadingData, setListViewLoadingData] = useState(!!listViewActionObj);

  const [defaultFilterParams, setDefaultFilterParams] = useState(defaultFilterParamsProp);
  const [filterParams, setFilterParams] = useState(defaultFilterParamsProp);
  const [customListDto, setCustomListDto] = useState(null);
  const [sortParams, setSortParams] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);
  const [removeModal, setRemoveModal] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [filterButtons, setFilterButtons] = useState(false);
  const [modalSize, setModalSize] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);
  const [showPrintSpinner, setShowPrintSpinner] = useState(false);
  const [showExportSpinner, setShowExportSpinner] = useState(false);
  const [view, setView] = useState(startingView);
  const [showAssignCareManagerModal, setShowAssignCareManagerModal] = useState(false);
  const [patientIds, setPatientIds] = useState(null);
  const [allPatientsChecked, setAllPatientsChecked] = useState(false);
  const [showCustomFilterList, setShowCustomFilterList] = useState(false);
  const [showUpdateCustomFilterList, setShowUpdateCustomFilterList] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [canceled, setCanceled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeoutRef = useRef(null);
  const previousSearchLengthRef = useRef(0);
  const latestQueryIdRef = useRef(0);
  const mountedRef = useRef(true);
  const serverTotalSizeRef = useRef(null);

  const LAST_QUERY_SETTINGS_KEY = `${quickListType}` + LAST_QUERY_SETTINGS_SUFFIX;
  const PRINT_THRESHOLD = AppPropertiesUtil.get("export.download.limit");
  const PRINT_THRESHOLD_PDF = AppPropertiesUtil.get("export.chart.download.limit");

  const setDefaultFilterParamHelper = (filterParams) => {
    setDefaultFilterParams(defaultFilterParamsProp);
    setFilterParams(filterParams);
  };

  const getEffectiveSearchTerm = (term) => {
    const raw = term ?? searchTerm ?? "";
    return raw.trim().length >= MIN_SEARCH_LENGTH ? raw : "";
  };

  const applySearchTermToDto = (dto, term, pageSizeOverride) => {
    dto.searchTerm = getEffectiveSearchTerm(term);
    if (pageSizeOverride !== undefined && pageSizeOverride !== null) {
      dto.pageSize = pageSizeOverride;
    }
    return dto;
  };

  const normalizeTotal = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : 0;
  };

  const getActiveTotalSize = () => {
    const total = normalizeTotal(totalSize);
    if (total > 0) {
      return total;
    }
    return normalizeTotal(
      (patientsList?.default?.length || 0) +
        (patientsList?.scheduledPatients?.length || 0) +
        (patientsList?.unScheduledPatients?.length || 0) +
        (patientsList?.unableToContact?.length || 0) +
        (patientsList?.patientRefused?.length || 0) +
        (patientsList?.leftMessage?.length || 0) +
        (patientsList?.changedPCP?.length || 0)
    );
  };

  const getSearchAwarePageSize = (term, allowFullSearchResults = false) => {
    const trimmed = (term ?? searchTerm ?? "").trim();
    if (allowFullSearchResults && trimmed.length >= MIN_SEARCH_LENGTH) {
      if (quickListType === "REFERRALS_BY_USER") {
        return basePageSize || pageSize;
      }
      const serverTotalSize = normalizeTotal(serverTotalSizeRef.current);
      return serverTotalSize || getActiveTotalSize() || pageSize;
    }
    return basePageSize || pageSize;
  };


  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleFilterChange = (filterParams, p = 1) => {
    cancelRequest(setCanceled);
    setFilterParams(filterParams);
    setSelectedPatients(new Map());
    const effectiveSize = getSearchAwarePageSize();
    setPageSize(basePageSize || pageSize);
    const dto = applySearchTermToDto(new PatientSearchDTO(filterParams, sortParams, p, effectiveSize, quickListType, totalSize), searchTerm, effectiveSize);
    query(dto, title, view);
  };

  const handleSortByChange = (e) => {
    const sortArr = e.target.value.split("-");
    let sortObj = { paramName: sortArr[0], direction: sortArr[1], priority: 1 };
    setSortParams([sortObj]);
    const effectiveSize = getSearchAwarePageSize();
    setPageSize(basePageSize || pageSize);
    const dto = applySearchTermToDto(new PatientSearchDTO(filterParams, [sortObj], pageNumber, effectiveSize, quickListType, totalSize), searchTerm, effectiveSize);
    query(dto, title, view);
  };

  const handlePaginationChange = (pageNumber) => {
    setPageNumber(pageNumber);
    const effectiveSize = getSearchAwarePageSize();
    setPageSize(basePageSize || pageSize);
    const dto = applySearchTermToDto(new PatientSearchDTO(filterParams, sortParams, pageNumber, effectiveSize, quickListType, totalSize), searchTerm, effectiveSize);
    query(dto, title, view);
  };

  const handleSearchChange = (value = "") => {
    const updatedValue = value;
    setSearchTerm(updatedValue);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const trimmed = updatedValue.trim();
    const previousLength = previousSearchLengthRef.current;
    const shouldReset =
      trimmed.length === 0 || (trimmed.length < MIN_SEARCH_LENGTH && previousLength >= MIN_SEARCH_LENGTH);
    if (shouldReset) {
      setPatientsList({
        default: [],
        scheduledPatients: [],
        unScheduledPatients: [],
        unableToContact: [],
        patientRefused: [],
        leftMessage: [],
        changedPCP: [],
      });
      setLoadingData(true);
      if (listViewActionObj) {
        setListViewLoadingData(true);
      }
    }
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH && previousLength < MIN_SEARCH_LENGTH) {
      previousSearchLengthRef.current = trimmed.length;
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      const currentTrimmed = updatedValue.trim();
      const currentLength = currentTrimmed.length;
      const shouldSearch = currentLength >= MIN_SEARCH_LENGTH;
      if (!shouldSearch && !shouldReset) {
        previousSearchLengthRef.current = currentLength;
        return;
      }
      const firstPage = 1;
      setPageNumber(firstPage);
      const effectiveSize = shouldSearch ? getSearchAwarePageSize(currentTrimmed) : basePageSize || pageSize;
      setPageSize(basePageSize || pageSize);
      const termToUse = shouldSearch ? updatedValue : "";
      const dto = applySearchTermToDto(
        new PatientSearchDTO(filterParams, sortParams, firstPage, effectiveSize, quickListType, totalSize),
        termToUse,
        effectiveSize
      );
      query(dto, title, view, termToUse);
      previousSearchLengthRef.current = currentLength;
    }, SEARCH_DELAY_MS);
  };
  let emptyStateMessage = "Loading...";
  if (!canceled) {
    emptyStateMessage = "No Patients Found";
  }
  if (searchTerm.trim().length >= MIN_SEARCH_LENGTH) {
    emptyStateMessage = "No patients found in this list";
  }
  const sectionPatientsByGapStatus = (patientListSectioned) => {
    let patientsWithNextVisit = [];
    let newUnscheduledData = patientListSectioned?.unscheduledData || [];
    if (!isEmpty(newUnscheduledData)) {
      for (const patient of newUnscheduledData) {
        if (patient.nextVisit) {
          patientsWithNextVisit.push(patient);
        }
      }
    }

    if (!isEmpty(patientsWithNextVisit)) {
      patientListSectioned.unScheduledPatients = patientsWithNextVisit;
    }

    return patientListSectioned;
  };

  const copyToPatientListVariables = (
    data,
    unscheduledData,
    unableToContact,
    leftMessage,
    changedPCP,
    patientRefused,
    inView
  ) => {
    let copyMap = null;
    if (!inView) {
      inView = view;
    }
    if (inView === VIEW.card || (inView === VIEW.list && !listViewActionObj)) {
      copyMap = cardViewActionObj.copyMap;
    } else if (inView === VIEW.list && listViewActionObj) {
      copyMap = listViewActionObj.copyMap;
    }
    const rawTotalSize = _.get(data, copyMap["totalSize"]);
    const rawPageSize = _.get(data, copyMap["pageSize"]);
    const rawPageNumber = _.get(data, copyMap["pageNumber"]);
    serverTotalSizeRef.current = rawTotalSize;
    let patientListSectioned = {
      default: [],
      scheduledPatients: [],
      unScheduledPatients: [],
      unableToContact: [],
      patientRefused: [],
      leftMessage: [],
      changedPCP: [],
    };
    const rawList = _.get(data, copyMap["list"]) || [];
    patientListSectioned.default = rawList;
    setTotalSize(rawTotalSize);
    setPageSize(rawPageSize);
    setPageNumber(rawPageNumber);
    patientListSectioned = sectionPatientsByGapStatus(patientListSectioned);
    setPatientsList(patientListSectioned);
  };

  useEffect(() => {
    if (cardViewData?.queryId !== latestQueryIdRef.current) {
      return;
    }
    if (!listViewActionObj) {
      setListViewData(cardViewData);
      copyToPatientListVariables(
        cardViewData.data,
        cardViewData.unscheduledData,
        cardViewData.unableToContact,
        cardViewData.leftMessage,
        cardViewData.changedPCP,
        cardViewData.patientRefused
      );
    } else if (listViewActionObj && view === VIEW.card) {
      copyToPatientListVariables(
        cardViewData.data,
        cardViewData.unscheduledData,
        cardViewData.unableToContact,
        cardViewData.leftMessage,
        cardViewData.changedPCP,
        cardViewData.patientRefused
      );
    }
    setLoadingData(false);
  // Card response hydration is intentionally keyed to these response/view triggers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardViewData, listViewActionObj, view]);

  useEffect(() => {
    if (!listViewActionObj || listViewData?.queryId !== latestQueryIdRef.current) {
      return;
    }
    if (view === VIEW.list) {
      copyToPatientListVariables(listViewData.data);
    }
    setListViewLoadingData(false);
  // List response hydration is intentionally keyed to these response/view triggers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listViewActionObj, listViewData, view]);

  const query = async (patientSearchDTO, updatedTitle, updatedView, overrideSearchTerm) => {
    setLoadingData(true);
    const queryId = ++latestQueryIdRef.current;
    setPageNumber(patientSearchDTO.pageNumber);
    patientSearchDTO.searchTerm = getEffectiveSearchTerm(overrideSearchTerm ?? searchTerm);
    patientSearchDTO.pageSize = patientSearchDTO.pageSize || basePageSize || pageSize;
    SessionStorageUtil.setObj(RECENT_QUERY_SETTINGS_KEY, LAST_QUERY_SETTINGS_KEY);
    SessionStorageUtil.setObj(LAST_QUERY_SETTINGS_KEY, {
      patientSearchDTO: patientSearchDTO,
      view: updatedView,
      title: updatedTitle,
    });
    if (listViewActionObj) {
      setListViewLoadingData(true);
    }
    cardViewActionObj
      .fetchData(patientSearchDTO)
      .then((res) => {
        const isLatest = mountedRef.current && queryId === latestQueryIdRef.current;
        if (res.data && isLatest) {
          const pageObject = {
            pageNumber: patientSearchDTO.pageNumber,
            data: res.data,
            queryId,
          };
          setCardViewData(pageObject);
        } else if (isLatest) {
          setLoadingData(false);
        }
        if (isLatest) {
          setCanceled(false);
        }
      })
      .catch((err) => {
        const isLatest = queryId === latestQueryIdRef.current;
        if (err?.message !== "cancel by user" && isLatest) {
          setCanceled(false);
        }
        if (isLatest) {
          setLoadingData(false);
        }
      });
    if (listViewActionObj) {
      listViewActionObj
        .fetchData(patientSearchDTO)
        .then((res) => {
          const isLatest = mountedRef.current && queryId === latestQueryIdRef.current;
          if (res.data && isLatest) {
            const pageObject = {
              pageNumber: patientSearchDTO.pageNumber,
              data: res.data,
              queryId,
            };
            setListViewData(pageObject);
          } else if (isLatest) {
            setListViewLoadingData(false);
          }
          if (isLatest) {
            setCanceled(false);
          }
        })
        .catch((err) => {
          const isLatest = queryId === latestQueryIdRef.current;
          if (err?.message !== "cancel by user" && isLatest) {
            setCanceled(false);
          }
          if (isLatest) {
            setListViewLoadingData(false);
          }
        });
    }
  };


  const fetchData = async (filterParams, quicklistName, title, view) => {
    const effectiveSize = getSearchAwarePageSize();
    query(new PatientSearchDTO(filterParams, sortParams, pageNumber, effectiveSize, quicklistName), title, view);
  };

  useEffect(() => {
    const restoreObject = SessionStorageUtil.getObj(CE_RESTORE_OBJ);
    if (restoreObject) {
      if (restoreObject.recentKey === LAST_QUERY_SETTINGS_KEY) {
        SessionStorageUtil.setObj(LAST_QUERY_SETTINGS_KEY, restoreObject.quickListObj);
      }
    }
    setInitialLoad(true);
  // Restore detection is intentionally keyed to route quicklist changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quicklistId]);

  useEffect(() => {
    if (!initialLoad) return;
    setInitialLoad(false);
    //restore from last timeout override
    const restoreObject = SessionStorageUtil.getObj(CE_RESTORE_OBJ);
    let quicklistObj = SessionStorageUtil.getObj(LAST_QUERY_SETTINGS_KEY);
    let useLastQuery = SessionStorageUtil.getObj(LAST_QUERY_USAGE_KEY);
    SessionStorageUtil.setObj(LAST_QUERY_USAGE_KEY, null);
    if (restoreObject) {
      if (restoreObject.recentKey === LAST_QUERY_SETTINGS_KEY) {
        quicklistObj = restoreObject.quickListObj;
        SessionStorageUtil.setObj(LAST_QUERY_SETTINGS_KEY, quicklistObj);
      }
      SessionStorageUtil.setObj(CE_RESTORE_OBJ, null);
    }
    if ((restoreObject || useLastQuery) && quicklistObj?.patientSearchDTO) {
      if (defaultSortParamsProp) {
        quicklistObj.patientSearchDTO.sortParams = defaultSortParamsProp;
      }
      //load from back button
      setTitle(quicklistObj.title);
      setView(quicklistObj.view);
      setFilterButtons(hasNumericQuicklistId(quicklistId));
      setDefaultFilterParamHelper(quicklistObj.patientSearchDTO.filterParams);
      setSortParams(quicklistObj.patientSearchDTO.sortParams);
      setSearchTerm(quicklistObj.patientSearchDTO.searchTerm ?? "");
      const restoredSize = getSearchAwarePageSize(quicklistObj.patientSearchDTO.searchTerm ?? "");
      quicklistObj.patientSearchDTO.pageSize = restoredSize;
      setPageSize(restoredSize);
      query(quicklistObj.patientSearchDTO, quicklistObj.title, quicklistObj.view, quicklistObj.patientSearchDTO.searchTerm ?? "");
    } else if (hasNumericQuicklistId(quicklistId)) {
      //load from custom list
      axios
        .get(CUSTOM_LIST_URL + "?id=" + quicklistId, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((response) => {
          setFilterButtons(true);
          setDefaultFilterParamHelper(response.data.patientRequestDTO.filterParams);
          setTitle(response.data.name);
          fetchData(response.data.patientRequestDTO.filterParams, response.data.quickList, response.data.name, view);
          setCustomListDto(response.data);
        })
        .catch((error) => {
          setModalTitle("Error");
          setModalSize("lg");
          setModalMessage(error?.response?.data?.message ?? error.message);
          setLoadingData(false);
        });
    } else if (quicklistId?.includes("customWorklist")) {
      //load member list
      axios
        .get(WORKLIST_URL + "?id=" + quicklistId, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((response) => {
          setCustomListDto(response.data);
        })
        .catch((error) => {
          setLoadingData(false);
        });

      setFilterButtons(true);
      let valuesAndDisplayValues = [];
      valuesAndDisplayValues.push({
        text: customWorklistName,
        value: quicklistId,
      });

      let filterParam = new FilterParam(
        "Member Worklist",
        "eq",
        valuesAndDisplayValues,
        (x) => x.value,
        (x) => x.text
      );
      const tempFilterParams = Object.assign([], filterParams);
      tempFilterParams.push(filterParam);
      setDefaultFilterParamHelper(tempFilterParams);
      setTitle(customWorklistName);
      fetchData(tempFilterParams, "ALL_PATIENTS", customWorklistName, view);
      setFilterParams(tempFilterParams);
    } else if (quicklistId?.includes("data")) {
      let patientFilters = preventiveCarePatientFilterParams;
      axios
        .get(process.env.REACT_APP_REST_API_BASE_URL + `/v1/patients/overview/summary?patientId=${quicklistId}`, {
          headers: {},
        })
        .then((response) => {
          patientFilters[0].displayValue[0] = response.data.lastName;
          patientFilters[0].displayValue[1] = response.data.firstName;
          patientFilters[0].paramValue[0] = response.data.lastName + ", " + response.data.firstName;
          patientFilters[1].displayValue[0] = "On " + formatDate(response.data.dateOfBirth);
          patientFilters[1].paramValue[0] = formatDate(response.data.dateOfBirth);

          setFilterButtons(true);
          setDefaultFilterParamHelper(patientFilters);
          setTitle(titleProp);
          fetchData(patientFilters, quickListType, titleProp, view);
        })
        .catch((error) => {
          setLoadingData(false);
        });
    } else {
      setTitle(titleProp);
      setDefaultFilterParamHelper(defaultFilterParamsProp);
      setFilterButtons(false);

      if (defaultSortParamsProp) {
        setSortParams(defaultSortParamsProp);
        const effectiveSize = getSearchAwarePageSize();
        query(
          new PatientSearchDTO(defaultFilterParamsProp, defaultSortParamsProp, pageNumber, effectiveSize, quickListType),
          titleProp,
          view
        );
      } else {
        fetchData(defaultFilterParamsProp, quickListType, titleProp, view);
      }
    }
  // Initial data load is intentionally gated by initialLoad to avoid repeated queries.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoad]);

  const reloadPatientView = () => {
    fetchData(filterParams, quickListType, titleProp, view);
  };

  const summary = () => {
    axios
      .get(SUMMARY_URL, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setShowPrintSpinner(false);
        setShowExportSpinner(false);
        setModalTitle("Export Summary");
        setModalSize("lg");
        setModalMessage(<ExportSummary exportSummary={response.data} />);
      })
      .catch((error) => {
        setShowPrintSpinner(false);
        setShowExportSpinner(false);
        setModalTitle("Error");
        setModalMessage(error.response.data.message);
        setLoadingData(false);
      });
  };

  const normalizeIdValue = (value) => {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : null;
  };

  const getPatientBaseIdentifier = (patient) => {
    if (!patient) return null;
    return (
      normalizeIdValue(patient.id) ??
      normalizeIdValue(patient.patientId) ??
      normalizeIdValue(patient.patientID) ??
      normalizeIdValue(patient.memberId) ??
      null
    );
  };

  const getPatientVisitIdentifier = (patient) => {
    if (!patient) return null;
    return (
      normalizeIdValue(patient.worklistItemId) ??
      normalizeIdValue(patient.worklistItemID) ??
      normalizeIdValue(patient.patientGapId) ??
      normalizeIdValue(patient.patientGapID) ??
      normalizeIdValue(patient.careGapId) ??
      normalizeIdValue(patient.careGapID) ??
      normalizeIdValue(patient.visitGapId) ??
      normalizeIdValue(patient.visitGapID) ??
      normalizeIdValue(patient.visitId) ??
      normalizeIdValue(patient.visitID) ??
      null
    );
  };

  const buildSelectedItemIdentifiers = () => {
    const baseIdentifiers = new Set();
    const fallbackIdentifiers = new Set();

    selectedPatients.forEach((value) => {
      const baseId = getPatientBaseIdentifier(value);
      if (baseId) {
        baseIdentifiers.add(baseId);
        return;
      }

      const visitId = getPatientVisitIdentifier(value);
      if (visitId) {
        fallbackIdentifiers.add(visitId);
      }
    });

    return [...baseIdentifiers, ...fallbackIdentifiers];
  };

  const print = () => {
    setShowPrintSpinner(true);
    const effectiveSize = getSearchAwarePageSize(searchTerm, true);
    const filterData = applySearchTermToDto(new PatientSearchDTO(filterParams, sortParams, pageNumber, effectiveSize, quickListType), searchTerm, effectiveSize);

    let criteria = {};
    criteria.patientRequest = filterData;
    criteria.selectedItems = buildSelectedItemIdentifiers();
    criteria.quickList = quickListType;
    axios
      .post(PRINT_URL, criteria, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        summary();
      })
      .catch((error) => {
        setShowPrintSpinner(false);
        setModalTitle("Error");
        setModalMessage(error.response.data.message);
      });
  };

  const exportPatients = () => {
    setShowExportSpinner(true);
    const effectiveSize = getSearchAwarePageSize(searchTerm, true);
    const filterData = applySearchTermToDto(new PatientSearchDTO(filterParams, sortParams, pageNumber, effectiveSize, quickListType), searchTerm, effectiveSize);
    let criteria = {};
    criteria.patientRequest = filterData;
    criteria.selectedItems = buildSelectedItemIdentifiers();
    criteria.quickList = quickListType;
    axios
      .post(exportPatientsUrl, criteria, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        summary();
      })
      .catch((error) => {
        setShowExportSpinner(false);
        setModalTitle("Error");
        setModalMessage(error.response.data.message);
      });
  };

  const getPatientSelectionKey = (patient) => {
    if (!patient) return null;

    const instanceKey = normalizeIdValue(patient.selectionInstanceKey);
    if (instanceKey) {
      return instanceKey;
    }

    const baseId =
      normalizeIdValue(patient.id) ??
      normalizeIdValue(patient.patientId) ??
      normalizeIdValue(patient.patientID) ??
      normalizeIdValue(patient.memberId) ??
      null;

    const gapId =
      normalizeIdValue(patient.careGapId) ??
      normalizeIdValue(patient.careGapID) ??
      normalizeIdValue(patient.patientGapId) ??
      normalizeIdValue(patient.patientGapID) ??
      normalizeIdValue(patient.visitGapId) ??
      normalizeIdValue(patient.visitGapID) ??
      normalizeIdValue(patient.visitId) ??
      normalizeIdValue(patient.visitID) ??
      normalizeIdValue(patient.gapId) ??
      normalizeIdValue(patient.gapID) ??
      normalizeIdValue(patient.worklistItemId) ??
      normalizeIdValue(patient.worklistItemID) ??
      null;

    if (baseId && gapId) {
      return `${baseId}|${gapId}`;
    }

    if (baseId) {
      return baseId;
    }

    if (gapId) {
      return `gap|${gapId}`;
    }

    return safeStringify(patient);
  };

  const getPatientSelectionBaseKey = (patient) => {
    if (!patient) return null;
    const patientCopy = { ...patient };
    delete patientCopy.selectionInstanceKey;
    return getPatientSelectionKey(patientCopy);
  };

  const updateSelectionForPatient = (selectedPatientsMap, patient, selected) => {
    const key = getPatientSelectionKey(patient);
    if (!key) {
      return;
    }

    const baseKey = getPatientSelectionBaseKey(patient);
    if (selected) {
      selectedPatientsMap.set(key, patient);
    } else {
      selectedPatientsMap.delete(key);
    }

    if (baseKey && baseKey !== key) {
      selectedPatientsMap.delete(baseKey);
    }
  };

  const selectPatient = (patient, selected) => {
    const selectedPatientsCopy = new Map(selectedPatients);
    updateSelectionForPatient(selectedPatientsCopy, patient, selected);
    setSelectedPatients(selectedPatientsCopy);
  };
  const selectPatients = (patients, selected) => {
    const selectedPatientsCopy = new Map(selectedPatients);
    for (const patient of patients) {
      updateSelectionForPatient(selectedPatientsCopy, patient, selected);
    }
    setSelectedPatients(selectedPatientsCopy);
  };

  const [selectAllLabel, setSelectAllLabel] = useState("Select All");

  const isPatientSelected = (patient) => {
    const key = getPatientSelectionKey(patient);
    if (!key) return false;
    if (selectedPatients.has(key)) {
      return true;
    }
    const baseKey = getPatientSelectionBaseKey(patient);
    return baseKey ? selectedPatients.has(baseKey) : false;
  };

  const filteredPatientList = patientsList;

  const selectOrUnSelectAll = (selected) => {
    const selectedPatients = new Map();
    setAllPatientsChecked(selected);

    if (selected) {
      filteredPatientList.default.forEach((x) => {
        const key = getPatientSelectionKey(x);
        if (!key) return;
        selectedPatients.set(key, x);
      });
    }
    setSelectedPatients(selectedPatients);

    if (selectAllLabel) {
      setSelectAllLabel(selectAllLabel === "Select All" ? "Deselect All" : "Select All");
    }
  };

  const removePatients = async () => {
    const uniquePatientIds = new Set();
    selectedPatients.forEach((patient) => {
      if (patient?.id !== undefined && patient?.id !== null) {
        uniquePatientIds.add(patient.id);
      }
    });
    await removePatientsFromList(Array.from(uniquePatientIds));
    setRemoveModal(false);
    query(new PatientSearchDTO(filterParams, sortParams, pageNumber, pageSize, quickListType), title, view);
  };

  const closeEvidenceModal = () => {
    query(new PatientSearchDTO(filterParams, sortParams, pageNumber, pageSize, quickListType), title, view);
  };

  const updateParent = (updatedRef, sourceType) => {
    let newState = patientsList.default;
    if (sourceType === "ManageEvidence") {
      return;
    }
    if (sourceType && sourceType === "SubmitRecentVisit") {
      newState.forEach((pat, patI) => {
        if (pat.id === updatedRef.patientId) {
          newState[patI].nextVisit = updatedRef.nextVisit;
          newState[patI].recentVisitStatus = updatedRef.recentVisitStatus;
          newState[patI].nextVisitInfo = updatedRef.nextVisitInfoDesc;
          newState[patI].recentVisit = updatedRef.recentVisit;
          newState[patI].recentVisitInfo = updatedRef.recentVisitInfoDesc;
          let patientListSectioned = {
            default: [],
            scheduledPatients: [],
            unScheduledPatients: [],
            unableToContact: [],
            patientRefused: [],
            leftMessage: [],
            changedPCP: [],
          };
          patientListSectioned.default = [...newState];
          setPatientsList(patientListSectioned);
        }
      });
    } else if (updatedRef) {
      let patIndex = null;
      let refIndex = null;
      newState.forEach((pat, patI) => {
        if (pat.id === updatedRef.patient.id) {
          patIndex = patI;
          pat.referralDtoList.forEach((ref, refI) => {
            if (ref.id === updatedRef.referralId) {
              refIndex = refI;
            }
          });
        }
      });
      if (newState[patIndex].referralDtoList[refIndex].status !== updatedRef.status) {
        newState[patIndex].referralDtoList[refIndex].status = updatedRef.status;
        let patientListSectioned = {
          default: [],
          scheduledPatients: [],
          unScheduledPatients: [],
          unableToContact: [],
          patientRefused: [],
          leftMessage: [],
          changedPCP: [],
        };
        patientListSectioned.default = [...newState];
        setPatientsList(patientListSectioned);
      }
    }
  };

  const onShowAssignCareManagerModal = (patientIds) => {
    setShowAssignCareManagerModal(true);
    setPatientIds(patientIds);
  };

  const bulkCareManagerAssignment = () => {
    if (!isEmpty(selectedPatients)) {
      setSelectedName("");
      setShowAssignCareManagerModal(true);
      const uniquePatientIds = new Set();
      selectedPatients.forEach((patient) => {
        if (patient?.id !== undefined && patient?.id !== null) {
          uniquePatientIds.add(patient.id);
        }
      });
      setPatientIds(Array.from(uniquePatientIds));
    }
  };

  const assignToCareManager = (assignment) => {
    const payload = patientIds.map((patientId) => {
      return { ...assignment, patientId, origin: "CARE_EMPOWER_REFERRALS" };
    });
    assignCareManager(payload)
      .then(() => {
        setSelectedPatients(new Map());
        setShowAssignCareManagerModal(false);
        setAllPatientsChecked(false);
        reloadPatientView();
      })
      .catch((error) => {
        onCareManagerAssigmentError(error);
        setShowAssignCareManagerModal(false);
      });
  };

  const customFilterLists = () => {
    setShowCustomFilterList(true);
  };
  const activeTotalSize = getActiveTotalSize();
  const disableExport = !!(
    isEmpty(activeTotalSize) || (activeTotalSize > PRINT_THRESHOLD_PDF && selectedPatients.size === 0)
  );
  const hasPatientResults =
    filteredPatientList.default && filteredPatientList?.default?.length > 0 && filteredPatientList.default[0] !== "Empty";
  const showPatientResults = hasPatientResults;
  const showEmptyState = !hasPatientResults && !loadingData && !listViewLoadingData;
  const patientResultsView = (
    <ListView
      updatePatients={setPatientsList}
      patientList={filteredPatientList}
      quickListType={quickListType}
      workListStatusSize={[
        scheduledPatientSize,
        unScheduledPatientSize,
        unableToContactPatientSize,
        patientRefusedPatientSize,
        leftMessagePatientSize,
        changedPCPPatientSize,
      ]}
      loadingData={loadingData}
      listViewLoadingData={listViewLoadingData}
      handleChangeCheckBox={selectPatient}
      handleAllPatientsCheckbox={selectPatients}
      isPatientSelected={isPatientSelected}
      quicklistId={quicklistId}
      returnToMyPatientsUrl={returnToMyPatientsUrl}
      updateParent={updateParent}
      closeEvidenceModal={closeEvidenceModal}
      reloadPatientView={reloadPatientView}
      onShowAssignCareManagerModal={onShowAssignCareManagerModal}
      careManagementActions={careManagementActions}
      titleProp={titleProp}
      handleFilterChange={handleFilterChange}
      filterParams={filterParams}
      pageNumber={pageNumber}
    />
  );

  return (
    <Container fluid className="py-3">
      <Helmet>
        <title>{title} | CareEmpower</title>
      </Helmet>
      <Modals
        show={removeModal}
        onHide={() => setRemoveModal(false)}
        title="Remove Member(s) from organization's patient list"
        size="lg"
        onCreate={removePatients}
        hideClickButton={false}
        showExtraButton={false}
        hideFooter={false}
        completeBtn="Save"
        completeBtnId={`${componentName}-saveButton`}
        body={<RemoveModalBody patientsSelected={Array.from(selectedPatients, ([key, value]) => value)} />}
      />
      {hasNumericQuicklistId(quicklistId) ? (
        <ShareWorklist
          shareModal={shareModal}
          setShareModal={setShareModal}
          setModalMessage={setModalMessage}
          setModalSize={setModalSize}
          setModalTitle={setModalTitle}
          customListDto={customListDto}
          setCustomListDto={setCustomListDto}
          quicklistId={quicklistId}
          customWorklistName={customWorklistName}
        />
      ) : (
        ""
      )}
      <ConfirmWorklistDeletion
        showConfirmation={showDeleteConfirmation}
        setConfirmation={setShowDeleteConfirmation}
        id={quicklistId}
        customListDto={customListDto}
      />
      <Row className="mb-3">
        <Col>
          <Row>
            <Col>
                <FilterGroupNew
                  key={quickListType + '-' + quicklistId}
                  filters={filters}
                  handleChange={handleFilterChange}
                  defaultFilterParams={defaultFilterParams}
                  filterParams={filterParams}
                  customWorklistName={SessionStorageUtil.getObj(LAST_QUERY_SETTINGS_KEY)?.title}
                  additionalValidation={qlsSettings.additionalFilterValidation}
                  filterGroups={{
                    'Attribution': [
                      'Patient Engagement',
                      'Market',
                      'Payor',
                      'PCP Name',
                      'PCP Practice',
                      'Provider MSA',
                      'Qualifying Programs',
                      AppPropertiesUtil.get("ecip.visit.monthly") ? 'Chronic Care Management Eligible' : 'Quarterly Visit Eligible',
                      'Risk Level',
                      'Roster Status',
                      'VBC Partners'
                    ],
                    'Care Management': [
                      'Care Program',
                      'Care Program Step Coordinator'
                    ],
                    'Demographics': [
                      'DOB',
                      'Expired Date',
                      'Patient City',
                      'Patient Expired',
                      'Patient Name',
                      'Patient Status',
                      'Patient Zip Code',
                      'State'
                    ],
                    'Preventive Care': [
                      'Care Opportunity Name',
                      'Care Opportunity Status',
                      'Care Opportunity Year',
                      'Chronic Condition',
                      'Last Known Visit',
                      'Suspect Status',
                      'W15 Approved',
                      'W30 Approved',
                    ],
                    'Quality and Risk': [
                      'Validation Status',
                      'Validator',
                      'Status Update Date',
                      'Validation Date',
                      'Date of Service',
                    ],
                    'Transitions of Care': [
                      'ADT Event Date',
                      'Days Since Last ADT',
                      'Event Type',
                      'Facility',
                      'Latest Discharge Date',
                      'Readmit'
                    ]
                  }}
                />
            </Col>
          </Row>
          <div className="d-flex justify-content-between gap-3 flex-wrap flex-md-nowrap">
            <div className="d-flex flex-nowrap flex-grow justify-content-between justify-content-md-start align-items-center w-100">
              <div>
                {view === VIEW.card && (
                  <Form.Check
                    checked={allPatientsChecked}
                    onChange={(e) => selectOrUnSelectAll(e.target.checked)}
                    type="checkbox"
                    id={`${componentName}-PatientSelect`}
                    className="d-inline-block ms-3 me-2"
                  />
                )}
                <Badge pill bg="light" id={`${componentName}-PatientCount`} className="border text-dark me-2">
                  {activeTotalSize.toLocaleString()} Patients
                </Badge>
              </div>
              <Form.Control
                type="search"
                placeholder="Search patients by name or ID"
                aria-label="Search patients within this list"
                id={`${componentName}-PatientSearch`}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="d-inline-block ms-2"
                style={{ maxWidth: "320px" }}
              />
              {quickListId && quickListId === "REFERRALS_CARE_MANAGEMENT" && (
                <DropdownButton
                  id={`${componentName}-actionsBtn`}
                  disabled={selectedPatients.size < 2}
                  variant="light"
                  text="secondary"
                  className="d-inline-block rounded border me-2"
                  title="Bulk Actions"
                >
                  {careManagementActions?.map((action, idx) => (
                    <Dropdown.Item
                      id={`${componentName}-actionsBtnItems-${idx}`}
                      key={`${componentName}-${action.value}-${idx}-key`}
                      onClick={() => bulkCareManagerAssignment()}
                    >
                      {action.text}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              )}
              {children}
              {(loadingData || listViewLoadingData || canceled) && (
                <SpinnerLoading />
              )}
            </div>
            <div className="d-flex flex-nowrap jusitfy-content-between justify-content-md-end align-items-center">
              {(showExportSpinner || showPrintSpinner) && (
                <SpinnerLoading />
              )}
              {!isEmpty(patientsList.default) && quickListId !== "REFERRALS_LIST" && (
                <div className="d-inline-block me-2 align-top">
                  {view === VIEW.card && (
                    <select
                      className="form-select min-w-230"
                      id={`${componentName}-Sorting`}
                      defaultValue="Sort by"
                      onChange={(e) => handleSortByChange(e)}
                    >
                      <option value="Sort by" disabled hidden>
                        Sort by
                      </option>
                      <option value="fullname-Asc">Name (A - Z)</option>
                      <option value="fullname-Desc">Name (Z - A)</option>
                      <option value="dateofbirth-Asc">Date of Birth (oldest first)</option>
                      <option value="dateofbirth-Desc">Date of Birth (youngest first)</option>
                      <option value="risk-Asc">Risk Score (lowest first)</option>
                      <option value="risk-Desc">Risk Score (highest first)</option>
                    </select>
                  )}
                </div>
              )}
              {quickListType !== "ACTIVE_INACTIVE_PATIENTS" && (
                <Button
                  variant="secondary"
                  size="sm"
                  className={`align-top border me-2 ${filterButtons ? "" : "d-none"}`}
                  id={`${componentName}-Share`}
                  onClick={() => setShareModal(true)}
                >
                  Share
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                className={`align-top border me-2 ${filterButtons ? "" : "d-none"}`}
                id={`${componentName}-Delete`}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                Delete
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className={`align-top border me-2 ${filterButtons ? "" : "d-none"}`}
                id={`${componentName}-Update`}
                onClick={() => setShowUpdateCustomFilterList(true)}
              >
                Update
              </Button>

              {!isEmpty(patientsList.default) && (
                <>
                  <div className="d-inline-block ms-0 ms-sm-2">
                    <CustomPagination
                      paginateButClicked={handlePaginationChange}
                      MAX_PAGE={Math.ceil(activeTotalSize / pageSize)}
                      pageNumber={Math.ceil(pageNumber)}
                    />
                  </div>{" "}
                </>
              )}
              {exportPatientsUrl != null && (
                <Dropdown className="d-inline-block align-top">
                  <Dropdown.Toggle id={`${componentName}-Export`} variant="link" className="border-0">
                    <i className="fa-solid fa-ellipsis text-dark"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="dark">
                    <Dropdown.Item id={`${componentName}-saveCustomList`} onClick={() => customFilterLists()}>
                      Save As Custom List
                    </Dropdown.Item>
                    {disableExport ? (
                      <OverlayTrigger
                        placement="bottom"
                        trigger={["hover", "focus"]}
                        popperConfig={{
                          modifiers: {
                            preventOverflow: { enabled: false },
                          },
                        }}
                        overlay={<Tooltip id="tooltip">{"Export allowed upto 200 members"}</Tooltip>}
                      >
                        <Dropdown.Item id={`${componentName}-ExportPDF`}>Export Chart Prep Tool</Dropdown.Item>
                      </OverlayTrigger>
                    ) : (
                      <Dropdown.Item
                        id={`${componentName}-ExportPDF`}
                        disabled={disableExport}
                        onClick={() => print()}
                      >
                        Export Chart Prep Tool
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      id={`${componentName}-ExportCSV`}
                      disabled={
                        isEmpty(activeTotalSize) ||
                        (activeTotalSize > PRINT_THRESHOLD && selectedPatients.size === 0)
                      }
                      onClick={() => exportPatients()}
                    >
                      Export to CSV
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
        </Col>
      </Row>
      {showPatientResults && patientResultsView}
      {showEmptyState && (
        <Row>
          <Col>
            <Card>
              <Card.Body>
                <Card.Text id={`${componentName}-NoPatients`}>
                  {emptyStateMessage}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      <CustomFilterList
        filters={getCurrentFilters(filters, filterParams)}
        showCustomFilterList={showCustomFilterList}
        setShowCustomFilterList={setShowCustomFilterList}
        filterParams={filterParams}
        sortParams={sortParams}
        pageNumber={pageNumber}
        pageSize={pageSize}
        quickList={quickListType}
        setModalMessage={setModalMessage}
        setModalSize={setModalSize}
        setModalTitle={setModalTitle}
      />
      <UpdateCustomFilterList
        id={quicklistId}
        filters={getCurrentFilters(filters, filterParams)}
        showUpdateCustomFilterList={showUpdateCustomFilterList}
        setShowUpdateCustomFilterList={setShowUpdateCustomFilterList}
        filterParams={filterParams}
        setModalMessage={setModalMessage}
        setModalSize={setModalSize}
        setModalTitle={setModalTitle}
      />
      <Modals
        show={modalMessage != null}
        onHide={() => setModalMessage(null)}
        title={modalTitle}
        size={modalSize || "md"}
        hideClickButton={true}
        showExtraButton={false}
        hideFooter={false}
        closeBtnId={`${componentName}-closeButton`}
        body={modalMessage}
      />
      <AssignCareManagerModal
        show={showAssignCareManagerModal}
        teams={careManagerTeams}
        programs={careManagerPrograms}
        onAssign={assignToCareManager}
        onClose={() => setShowAssignCareManagerModal(false)}
        onError={onCareManagerAssigmentError}
        selectedName={selectedName}
      />
    </Container>
  );
};

QuickLists.propTypes = {
  titleProp: PropTypes.string,
  filters: PropTypes.array,
  pageNumberProp: PropTypes.number,
  pageSizeProp: PropTypes.number,
  totalSizeProp: PropTypes.number,
  defaultFilterParamsProp: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  defaultSortParamsProp: PropTypes.array,
  exportPatientsUrl: PropTypes.string,
  quickListType: PropTypes.string,
  displayExtraRows: PropTypes.bool,
  quickListId: PropTypes.string,
  cardViewActionObj: PropTypes.shape({
    fetchData: PropTypes.func,
    copyMap: PropTypes.object,
  }),
  unScheduledActionObj: PropTypes.shape({
    fetchData: PropTypes.func,
    copyMap: PropTypes.object,
  }),
  listViewActionObj: PropTypes.shape({
    fetchData: PropTypes.func,
    copyMap: PropTypes.object,
  }),
  startingView: PropTypes.string,
  listViewColumns: PropTypes.array,
  children: PropTypes.node,
  careManagementActions: PropTypes.array,
  onCareManagerAssigmentError: PropTypes.func,
  careManagerPrograms: PropTypes.array,
  careManagerTeams: PropTypes.array,
  showCount: PropTypes.bool,
};

export { QuickLists, VIEW, LAST_QUERY_SETTINGS_SUFFIX, RECENT_QUERY_SETTINGS_KEY };
